import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import MediaCard from "@/components/feature/MediaCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchContent, formatDate } from "@/lib/content";
import { mediaList } from "@/mocks/media";
import { specialList } from "@/mocks/specials";

const PER_PAGE = 20;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [page, setPage] = useState(1);

  usePageMeta(
    keyword ? `搜索“${keyword}” - 蜜桃传媒` : "搜索 - 蜜桃传媒",
    `在蜜桃传媒搜索影视作品与娱乐资讯${keyword ? `，当前关键词：${keyword}` : ""}。`
  );

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = input.trim();
      if (next === keyword) return;
      setSearchParams(next ? { q: next } : {}, { replace: true });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [input, keyword, setSearchParams]);

  const results = useMemo(() => searchContent(keyword), [keyword]);
  const totalPages = Math.ceil(results.length / PER_PAGE);
  const paged = results.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const hotMedia = [...mediaList].sort((a, b) => b.rating - a.rating).slice(0, 5);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchParams(input.trim() ? { q: input.trim() } : {}, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <header className="mt-4 rounded-lg bg-secondary-100 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
          站内搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-700 md:text-base">
          输入关键词，查找影视作品、娱乐资讯与明星动态标题。
        </p>
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              name="search-keyword"
              placeholder="例如：悬疑、都市、采访、纪录片"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none md:text-base"
            />
            {input && (
              <button
                type="button"
                aria-label="清空关键词"
                onClick={() => setInput("")}
                className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-500 hover:bg-background-200"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
        {keyword && (
          <p className="mt-4 text-sm text-foreground-700">
            当前关键词：
            <strong className="text-primary-700">“{keyword}”</strong>
            <span className="ml-2 text-foreground-500">
              共找到 {results.length} 条结果
            </span>
          </p>
        )}
      </header>

      {!keyword && (
        <section className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
            <i className="ri-fire-line text-primary-600"></i>
            热门推荐
          </h2>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                {hotMedia.slice(0, 4).map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </div>
            <aside className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <h3 className="mb-4 font-heading text-base font-bold text-foreground-950">
                热门专题
              </h3>
              <ul className="flex flex-col gap-3">
                {specialList.slice(0, 4).map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/special/${item.id}`}
                      className="group flex items-center gap-3 rounded-md p-1 transition-colors hover:bg-background-200"
                    >
                      <div className="relative h-12 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title}专题封面`}
                          title={`${item.title}精选专题`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>
      )}

      {keyword && results.length > 0 && (
        <section className="mt-8">
          <p className="mb-4 text-sm text-foreground-600">
            第 {page} / {totalPages} 页
          </p>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {paged.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:gap-4 md:p-4"
              >
                <div
                  className="relative w-28 shrink-0 overflow-hidden rounded-md bg-background-100 sm:w-32"
                  style={{ aspectRatio: item.kind === "media" ? "2 / 3" : "4 / 3" }}
                >
                  <img
                    src={item.cover}
                    alt={`${item.title} 配图`}
                    title={`${item.title} 搜索结果`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-bold text-primary-700">
                      {item.kind === "media" ? "影视作品" : "影视资讯"}
                    </span>
                    <span className="text-[11px] text-foreground-500">
                      {formatDate(item.date)}
                    </span>
                  </div>
                  <h3 className="mt-1.5 font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-base">
                    {item.title}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                    {item.summary}
                  </p>
                  <p className="mt-auto pt-2 text-[11px] text-foreground-500">{item.extra}</p>
                </div>
              </Link>
            ))}
          </div>
          <Pagination current={page} total={totalPages} onChange={setPage} />
        </section>
      )}

      {keyword && results.length === 0 && (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-lg font-black text-foreground-950">
            未找到相关内容
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            换个关键词试试，或从下面的热门推荐开始浏览。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/category/movie"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
            >
              浏览电影
            </Link>
            <Link
              to="/category/tv"
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-800 hover:border-primary-300"
            >
              浏览电视剧
            </Link>
            <Link
              to="/star-news"
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-800 hover:border-primary-300"
            >
              明星动态
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}